// ─────────────────────────────────────────────────────────
// Configurator gauge presets — the single source of truth.
//
// These are AURORA'S OWN SETS, transcribed from the Shopify product catalog
// (Product_SKU_Catalog.xlsx / products_export_1.csv → catalog_products), using
// Aurora's own gauge names. They are deliberately NOT generic industry
// presets: an earlier version of this file carried D'Addario-style sets
// ("Super Light", "Regular", "Standard 6", "Extra-High (XHT)") whose middle
// gauges did not match anything Aurora actually winds.
//
// Rules for editing this file:
//   * Keep the FULL ordered gauge sequence. Endpoints alone are not a set —
//     Aurora ships sets that share a first and last gauge but differ in the
//     middle (electric 12-52 Extra-Heavy is 12/15/24/…, not 12/16/24/…).
//   * String count is `values.length`. It is NOT the gauge and must never be
//     derived from one.
//   * `construction` is per string and explicit. Do not infer it from position:
//     the guitar family is not universally "three plain then three wound", and
//     classical/ukulele strings are nylon, not plain steel.
//   * `source: "catalog"` means the set exists as an Aurora product.
//     `source: "extrapolated"` means the configurator offers it as a custom
//     build but no Aurora catalog set backs it — the UI must not present its
//     specification as verified.
//
// Shared by the configurator reference data (src/lib/configurator/data.ts) and
// the DB seed (prisma/seed.ts). This module MUST stay dependency-free — the
// seed imports it directly via a relative path.
// ─────────────────────────────────────────────────────────

/** Winding value persisted on GaugePresetString.windingType (DB enum). */
export type PresetWinding = "plain" | "round_wound";

/**
 * How a single string is built. Distinct from PresetWinding because "plain"
 * in the database covers both a plain steel guitar string and a nylon treble,
 * which are not the same product and must not be described the same way.
 */
export type StringConstruction =
  | "plain-steel"
  | "wound"
  | "nylon"
  | "nylon-core-wound";

/** Customer-facing label for a construction value. */
export const CONSTRUCTION_LABEL: Record<StringConstruction, string> = {
  "plain-steel": "Plain steel",
  wound: "Wound",
  nylon: "Nylon",
  "nylon-core-wound": "Wound, nylon core",
};

/** Short marker appended after a gauge in compact lists (".046w"). */
export const CONSTRUCTION_SUFFIX: Record<StringConstruction, string> = {
  "plain-steel": "",
  wound: "w",
  nylon: "n",
  "nylon-core-wound": "nw",
};

export interface PresetDef {
  /** Aurora's own name for the set. */
  name: string;
  /** Full ordered gauge sequence, inch decimals, low string index 0 = 1st string. */
  values: string[];
  /** Per-string construction, same order and length as `values`. */
  construction: StringConstruction[];
  /** Whether an Aurora catalog product backs this exact set. */
  source: "catalog" | "extrapolated";
  /** Why an extrapolated set exists, shown to the customer. */
  note?: string;
}

const P = "plain-steel" as const;
const W = "wound" as const;
const NY = "nylon" as const;
const NW = "nylon-core-wound" as const;

export const PRESET_SETS: Record<string, PresetDef[]> = {
  // Aurora electric sets — six-string catalog, ordered lightest first.
  electric: [
    { name: "Light",       values: ["0.009", "0.011", "0.016", "0.024", "0.032", "0.042"], construction: [P, P, P, W, W, W], source: "catalog" },
    { name: "Light-Heavy", values: ["0.009", "0.011", "0.016", "0.026", "0.036", "0.046"], construction: [P, P, P, W, W, W], source: "catalog" },
    { name: "Medium",      values: ["0.010", "0.013", "0.017", "0.026", "0.036", "0.046"], construction: [P, P, P, W, W, W], source: "catalog" },
    { name: "Big-Heavy",   values: ["0.010", "0.013", "0.017", "0.032", "0.042", "0.052"], construction: [P, P, P, W, W, W], source: "catalog" },
    { name: "Heavy",       values: ["0.011", "0.014", "0.018", "0.028", "0.038", "0.050"], construction: [P, P, P, W, W, W], source: "catalog" },
    { name: "Extra-Heavy", values: ["0.012", "0.015", "0.024", "0.032", "0.042", "0.052"], construction: [P, P, P, W, W, W], source: "catalog" },
    // 7- and 8-string: Aurora has no catalog product at these counts. These
    // extend the Medium set downward so the non-standard build path has a
    // starting point; the extra gauges are not an Aurora specification.
    {
      name: "Medium + low B",
      values: ["0.010", "0.013", "0.017", "0.026", "0.036", "0.046", "0.060"],
      construction: [P, P, P, W, W, W, W],
      source: "extrapolated",
      note: "Aurora's 10-46 Medium set with a seventh string added. Built to order — no stocked 7-string set.",
    },
    {
      name: "Extended range 8",
      values: ["0.010", "0.013", "0.017", "0.026", "0.036", "0.046", "0.060", "0.080"],
      construction: [P, P, P, W, W, W, W, W],
      source: "extrapolated",
      note: "Aurora's 10-46 Medium set extended to eight strings. Built to order — no stocked 8-string set.",
    },
  ],

  // Aurora acoustic sets — six-string catalog.
  acoustic: [
    { name: "Light",        values: ["0.010", "0.014", "0.022", "0.030", "0.038", "0.048"], construction: [P, P, P, W, W, W], source: "catalog" },
    { name: "Medium-Light", values: ["0.011", "0.015", "0.022", "0.030", "0.040", "0.050"], construction: [P, P, P, W, W, W], source: "catalog" },
    { name: "Medium",       values: ["0.012", "0.016", "0.024", "0.032", "0.042", "0.054"], construction: [P, P, P, W, W, W], source: "catalog" },
  ],

  // Aurora bass sets. Every bass string is wound.
  bass: [
    { name: "Light",   values: ["0.040", "0.060", "0.080", "0.100"],                     construction: [W, W, W, W],       source: "catalog" },
    { name: "Medium",  values: ["0.045", "0.065", "0.085", "0.105"],                     construction: [W, W, W, W],       source: "catalog" },
    { name: "Heavy",   values: ["0.050", "0.070", "0.090", "0.110"],                     construction: [W, W, W, W],       source: "catalog" },
    { name: "Light 5", values: ["0.040", "0.060", "0.080", "0.100", "0.120"],            construction: [W, W, W, W, W],    source: "catalog" },
    { name: "Medium 5", values: ["0.045", "0.065", "0.085", "0.105", "0.125"],           construction: [W, W, W, W, W],    source: "catalog" },
    { name: "Heavy 5", values: ["0.050", "0.070", "0.090", "0.110", "0.130"],            construction: [W, W, W, W, W],    source: "catalog" },
    { name: "Light 6", values: ["0.030", "0.040", "0.060", "0.080", "0.100", "0.120"],   construction: [W, W, W, W, W, W], source: "catalog" },
    { name: "Medium 6", values: ["0.030", "0.045", "0.065", "0.085", "0.105", "0.125"],  construction: [W, W, W, W, W, W], source: "catalog" },
  ],

  // Aurora classical sets. Nylon trebles (1-3), metal-wound nylon-core basses
  // (4-6). Classical sets are chosen by tension, not by a gauge range: the
  // sequence is not monotonic, so a "28-45" range label is meaningless here.
  classical: [
    { name: "Normal Tension", values: ["0.028", "0.032", "0.040", "0.029", "0.035", "0.043"], construction: [NY, NY, NY, NW, NW, NW], source: "catalog" },
    { name: "High Tension",   values: ["0.028", "0.032", "0.040", "0.031", "0.037", "0.045"], construction: [NY, NY, NY, NW, NW, NW], source: "catalog" },
  ],

  // Aurora ukulele sets, by instrument size. Nylon throughout. The fourth
  // value is lower than the third because these are re-entrant (high-G)
  // sets — the 4th string is tuned above the 3rd, not below it.
  ukulele: [
    { name: "Soprano", values: ["0.024", "0.029", "0.036", "0.025"], construction: [NY, NY, NY, NY], source: "catalog" },
    { name: "Concert", values: ["0.025", "0.031", "0.037", "0.027"], construction: [NY, NY, NY, NY], source: "catalog" },
    { name: "Tenor",   values: ["0.026", "0.033", "0.038", "0.028"], construction: [NY, NY, NY, NY], source: "catalog" },
  ],
};

// Preset names a given line does NOT offer, keyed by "<category>:<line slug>".
// Most lines carry the full set for their category; these are the exceptions
// Jake confirmed in the 2026-09 catalog review.
const PRESETS_NOT_OFFERED: Record<string, string[]> = {
  "electric:stainless-steel": ["Heavy", "Extra-Heavy", "Medium + low B", "Extended range 8"],
  "electric:prime": ["Heavy", "Extra-Heavy", "Medium + low B", "Extended range 8"],
  "bass:stainless-steel": ["Heavy"],
};

/** Presets offered for one instrument category under one line. */
export function presetsForLine(category: string, lineSlug: string): PresetDef[] {
  const sets = PRESET_SETS[category] ?? [];
  const excluded = PRESETS_NOT_OFFERED[`${category}:${lineSlug}`];
  return sets.filter((set) => set.source !== "extrapolated" && !excluded?.includes(set.name));
}

/** Map a construction value onto the winding enum persisted in the database. */
export function windingForConstruction(construction: StringConstruction): PresetWinding {
  return construction === "wound" || construction === "nylon-core-wound"
    ? "round_wound"
    : "plain";
}

/**
 * Winding for a string at a given position, by instrument category.
 *
 * Fallback only — used when no Aurora preset matches the gauge sequence in
 * hand. Prefer `constructionForGauges`, which returns the real per-string
 * construction from the catalog, and returns null rather than guessing.
 */
export function windingForPosition(category: string, index: number): PresetWinding {
  if (category === "electric" || category === "acoustic" || category === "classical") {
    return index < 3 ? "plain" : "round_wound";
  }
  if (category === "ukulele") return "plain";
  return "round_wound";
}

function thousandths(value: string | number): number {
  const n = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(n)) return NaN;
  return n >= 1 ? n : Number((n * 1000).toPrecision(15));
}

/**
 * Per-string construction for an exact gauge sequence, looked up against
 * Aurora's own sets.
 *
 * Returns null when no Aurora set matches — the caller must then describe the
 * set without construction markers rather than inventing them.
 */
export interface ApprovedPresetConstruction {
  id: string;
  category: string;
  lineSlug: string;
  approvalReference: string;
  strings: { gaugeValue: string; construction: StringConstruction; windingType: "plain" | "round_wound" | "flat_wound" | "half_wound" }[];
}

export function constructionForGauges(
  category: string,
  gauges: (string | number)[],
  context?: { lineSlug: string; preset: ApprovedPresetConstruction },
): StringConstruction[] | null {
  // Catalog gauge transcription is not evidence for manufacturing construction.
  if (!context?.preset.id || !context.preset.approvalReference.trim() ||
      context.preset.category !== category || context.preset.lineSlug !== context.lineSlug) return null;
  const target = gauges.map(thousandths);
  if (target.some(Number.isNaN)) return null;
  const strings = context.preset.strings;
  return strings.length === target.length && strings.every((s, i) => thousandths(s.gaugeValue) === target[i])
    ? strings.map((s) => s.construction) : null;
}

/** Find Aurora's own name for an exact gauge sequence, or null. */
export function presetForGauges(
  category: string,
  gauges: (string | number)[],
): PresetDef | null {
  const target = gauges.map(thousandths);
  if (target.some(Number.isNaN)) return null;
  return (
    (PRESET_SETS[category] ?? []).find(
      (set) =>
        set.values.length === target.length &&
        set.values.every((v, i) => thousandths(v) === target[i]),
    ) ?? null
  );
}

/**
 * "9-42" range label from an ordered inch-decimal value list.
 *
 * Only meaningful for sets whose gauges ascend. Classical and ukulele sets are
 * not ordered by thickness (re-entrant tuning, nylon trebles thicker than the
 * wound 4th), so `usesGaugeRange` gates this — those families are identified by
 * tension or instrument size instead.
 */
export function rangeLabel(values: string[]): string {
  return `${thousandths(values[0])}-${thousandths(values[values.length - 1])}`;
}

/** True when a "low-high" gauge range is a valid way to name this family's sets. */
export function usesGaugeRange(category: string): boolean {
  return category === "electric" || category === "acoustic" || category === "bass";
}

/** The label a family actually uses to identify a set. */
export function setLabel(category: string, set: PresetDef): string {
  return usesGaugeRange(category) ? rangeLabel(set.values) : set.name;
}
